import type { Permission, PlatformRole, Role, SubscriptionStatus } from '@kortex/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, setAccessToken, setUnauthenticatedHandler } from './api';

export interface PropertySummary {
  id: string;
  name: string;
  code: string;
  city?: string;
  role: Role;
}

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  mustChangePassword?: boolean;
  /** Whether this account asks for an authenticator code at sign-in. */
  twoFactorEnabled?: boolean;
}

export interface SubscriptionSummary {
  planCode: string;
  planName: string;
  priceMonthly: number;
  status: SubscriptionStatus;
  statusLabel: string;
  isEntitled: boolean;
  daysRemaining: number;
  endsAt: string | null;
  limits: { maxProperties: number; maxUsers: number };
}

export interface Session {
  user: SessionUser;
  property: { id: string; name: string; code?: string; gstin: string; stateCode: string } | null;
  role: Role | null;
  permissions: Permission[];
  platformRole: PlatformRole;
  properties: PropertySummary[];
}

export interface CaptchaFields {
  captchaId?: string;
  captchaAnswer?: string;
  /** Google's token, when reCAPTCHA is the challenge in use. */
  captchaToken?: string;
}

export interface RegisterInput {
  business: Record<string, string>;
  owner: Record<string, string>;
  planCode?: string;
  /** Emailed to the business address; absent until a superadmin sets up SMTP. */
  otp?: string;
  /** Only true parses on the server: registration is a contract. */
  acceptTerms?: boolean;
}

interface AuthValue {
  session: Session | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  /** The tenant's subscription, refreshed alongside the session. */
  subscription: SubscriptionSummary | null;
  isPlatformAdmin: boolean;
  refreshSubscription: () => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  signIn: (
    email: string,
    password: string,
    captcha?: CaptchaFields,
    totp?: string,
  ) => Promise<void>;
  /** Set when a session ended for a reason the sign-in screen should explain. */
  signedOutReason: string | null;
  signOut: () => Promise<void>;
  switchProperty: (propertyId: string) => Promise<void>;
  can: (...permissions: Permission[]) => boolean;
  canAny: (...permissions: Permission[]) => boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  /** Why the last session ended, when it is worth telling somebody. */
  const [signedOutReason, setSignedOutReason] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(null);
  const [status, setStatus] = useState<AuthValue['status']>('loading');

  const loadSubscription = useCallback(async () => {
    try {
      setSubscription(await api.get<SubscriptionSummary>('/api/subscription'));
    } catch {
      // Platform staff have no tenant subscription to read; the banner just
      // stays hidden rather than blocking the session.
      setSubscription(null);
    }
  }, []);

  const loadMe = useCallback(async () => {
    const me = await api.get<Session>('/api/auth/me');
    setSession(me);
    setStatus('authenticated');
    if (me.property) await loadSubscription();
    else setSubscription(null);
  }, [loadSubscription]);

  // On boot, try the refresh cookie so a reload keeps the user signed in.
  useEffect(() => {
    let cancelled = false;
    setUnauthenticatedHandler((reason) => {
      setSession(null);
      setStatus('anonymous');
      setSignedOutReason(
        reason === 'SESSION_IDLE'
          ? 'You were signed out because this screen was left idle. Sign in again.'
          : null,
      );
    });

    void (async () => {
      const refreshed = await api.refresh();
      if (cancelled) return;
      if (!refreshed) {
        setStatus('anonymous');
        return;
      }
      try {
        await loadMe();
      } catch {
        if (!cancelled) setStatus('anonymous');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadMe]);

  const signIn = useCallback(
    async (email: string, password: string, captcha?: CaptchaFields, totp?: string) => {
      const result = await api.post<{ accessToken: string }>('/api/auth/login', {
        email,
        password,
        ...(captcha?.captchaId ? { captchaId: captcha.captchaId } : {}),
        ...(captcha?.captchaAnswer ? { captchaAnswer: captcha.captchaAnswer } : {}),
        ...(captcha?.captchaToken ? { captchaToken: captcha.captchaToken } : {}),
        ...(totp ? { totp } : {}),
      });
      setAccessToken(result.accessToken);
      setSignedOutReason(null);
      await loadMe();
    },
    [loadMe],
  );

  const register = useCallback(
    async (input: RegisterInput) => {
      const result = await api.post<{ accessToken: string }>('/api/public/register', input);
      setAccessToken(result.accessToken);
      await loadMe();
    },
    [loadMe],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      setAccessToken(null);
      setSession(null);
      setSubscription(null);
      setStatus('anonymous');
    }
  }, []);

  const switchProperty = useCallback(
    async (propertyId: string) => {
      const result = await api.post<{ accessToken: string }>('/api/auth/switch-property', {
        propertyId,
      });
      setAccessToken(result.accessToken);
      await loadMe();
    },
    [loadMe],
  );

  const value = useMemo<AuthValue>(
    () => ({
      session,
      status,
      subscription,
      isPlatformAdmin: session ? session.platformRole !== 'NONE' : false,
      refreshSubscription: loadSubscription,
      register,
      signIn,
      signedOutReason,
      signOut,
      switchProperty,
      can: (...permissions) => permissions.every((p) => session?.permissions.includes(p) ?? false),
      canAny: (...permissions) =>
        permissions.some((p) => session?.permissions.includes(p) ?? false),
    }),
    [
      session,
      status,
      subscription,
      signedOutReason,
      loadSubscription,
      register,
      signIn,
      signOut,
      switchProperty,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}

/** Renders children only when the signed-in role holds the permission. */
export function Can({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission | Permission[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { can } = useAuth();
  const list = Array.isArray(permission) ? permission : [permission];
  return <>{can(...list) ? children : fallback}</>;
}
